import { CROPS, QUALITY_LABELS } from "./catalog";
import { clamp } from "./math";
import type { CropId, CropStage, FarmPlot, Inventory, Weather } from "./types";

const stageFromGrowth = (growth: number): CropStage => {
  if (growth <= 0) return "seed";
  if (growth < 0.2) return "seed";
  if (growth < 0.45) return "sprout";
  if (growth < 0.72) return "leaf";
  if (growth < 1) return "flower";
  return "mature";
};

export const createEmptyPlot = (
  id: number,
  x: number,
  y: number,
): FarmPlot => ({
  id,
  pos: { x, y },
  cropId: null,
  growth: 0,
  moisture: 0.58,
  fertility: 0.58,
  care: 0,
  careSamples: 0,
  quality: 0.5,
  stage: "empty",
  pulse: Math.random() * Math.PI * 2,
  dryWarned: false,
});

export const plantCrop = (plot: FarmPlot, cropId: CropId): void => {
  plot.cropId = cropId;
  plot.growth = 0.015;
  plot.care = 0;
  plot.careSamples = 0;
  plot.quality = 0.5;
  plot.stage = "seed";
  plot.dryWarned = false;
};

export const clearPlot = (plot: FarmPlot): void => {
  plot.cropId = null;
  plot.growth = 0;
  plot.care = 0;
  plot.careSamples = 0;
  plot.quality = 0.5;
  plot.stage = "empty";
  plot.dryWarned = false;
  plot.fertility = clamp(plot.fertility - 0.08, 0, 1);
};

export interface PlotTickResult {
  stageChanged: boolean;
  matured: boolean;
  becameDry: boolean;
}

/**
 * 农作物只依赖稳定的归一化状态，渲染与交互不会参与计算。
 * 这样后续接服务器时，可以在服务端复用同一套成长规则而不信任客户端动画。
 */
export const tickPlot = (
  plot: FarmPlot,
  deltaSeconds: number,
  weather: Weather,
): PlotTickResult => {
  const oldStage = plot.stage;
  const wasDry = plot.moisture < 0.16;
  const crop = plot.cropId ? CROPS[plot.cropId] : null;

  const rainGain =
    weather === "rain" ? 0.045 : weather === "drizzle" ? 0.016 : 0;
  plot.moisture = clamp(plot.moisture + rainGain * deltaSeconds, 0, 1);

  if (!crop || plot.stage === "mature") {
    if (!crop) plot.stage = "empty";
    return {
      stageChanged: oldStage !== plot.stage,
      matured: false,
      becameDry: false,
    };
  }

  plot.moisture = clamp(plot.moisture - crop.waterUse * deltaSeconds, 0, 1);
  plot.fertility = clamp(
    plot.fertility - (crop.id === "chili" ? 0.0034 : 0.0018) * deltaSeconds,
    0,
    1,
  );

  const moistureFitness =
    plot.moisture < 0.12
      ? 0
      : clamp(1 - Math.abs(plot.moisture - 0.66) * 1.45, 0.3, 1);
  const fertilityFitness = 0.42 + plot.fertility * 0.58;
  const weatherBonus =
    weather === "drizzle" ? 1.08 : weather === "rain" ? 1.03 : 1;
  const growthRate =
    (1 / crop.growSeconds) * moistureFitness * fertilityFitness * weatherBonus;

  plot.growth = clamp(plot.growth + growthRate * deltaSeconds, 0, 1);
  const careNow = clamp(moistureFitness * 0.68 + plot.fertility * 0.32, 0, 1);
  plot.care += careNow * deltaSeconds;
  plot.careSamples += deltaSeconds;
  const averageCare = plot.careSamples > 0 ? plot.care / plot.careSamples : 0.5;
  plot.quality = clamp(0.22 + averageCare * 0.78, 0, 1);
  plot.stage = stageFromGrowth(plot.growth);

  return {
    stageChanged: oldStage !== plot.stage,
    matured: oldStage !== "mature" && plot.stage === "mature",
    becameDry: !wasDry && plot.moisture < 0.16,
  };
};

export const waterPlot = (plot: FarmPlot, inventory: Inventory): boolean => {
  if (inventory.water < 1 || plot.moisture >= 0.96) return false;
  inventory.water -= 1;
  plot.moisture = clamp(plot.moisture + 0.48, 0, 1);
  plot.dryWarned = false;
  return true;
};

export const fertilizePlot = (
  plot: FarmPlot,
  inventory: Inventory,
): boolean => {
  if (inventory.fertilizer < 1 || plot.fertility >= 0.96) return false;
  inventory.fertilizer -= 1;
  plot.fertility = clamp(plot.fertility + 0.44, 0, 1);
  return true;
};

export const qualityTier = (quality: number): number => {
  if (quality >= 0.9) return 3;
  if (quality >= 0.72) return 2;
  if (quality >= 0.48) return 1;
  return 0;
};

export const qualityLabel = (quality: number): string =>
  QUALITY_LABELS[qualityTier(quality)];

export const harvestYield = (
  plot: FarmPlot,
): { food: number; seeds: number; bonusDew: number } => {
  if (!plot.cropId || plot.stage !== "mature")
    return { food: 0, seeds: 0, bonusDew: 0 };
  const crop = CROPS[plot.cropId];
  const multiplier = 0.72 + plot.quality * 0.78;
  return {
    food: Math.max(1, Math.round(crop.foodYield * multiplier)),
    seeds: Math.max(
      1,
      Math.round(crop.seedYield * (0.8 + plot.quality * 0.45)),
    ),
    bonusDew: plot.cropId === "moonherb" && plot.quality >= 0.82 ? 1 : 0,
  };
};
